import { FocusableElement } from '@react-types/shared';
import { useRef } from 'react';
import {
  AriaLinkOptions,
  mergeProps,
  useHover,
  useLink as useAriaLink,
} from 'react-aria';

export type UseLinkOptions = AriaLinkOptions;

// This component exists to serve both UnstyledLink and Link.
//
// The Link componet is a bit tricky because it uses both the `as` and `forwardedAs`
// props.  For example, props can be set on `Link` which cause it to be rendered
// as a Typography component using the `as` prop - this keeps all of the styles
// defined on Link while rendering it as another component.  You can, however, also
// pass an `as` prop (which defaults to an anchor tag).  In this case,
// the anchor tag is passed as `forwardedAs`, meaning the Typography component is
// then rendered as an anchor tag.
//
// This multi-level component replacement is difficult if Link has to render
// UnstyledLink, for example.  It's easier to have both components share the same
// hook rather than composing Link using UnstyledLink
/**
 * A hook used to build link components
 */
export default function useLink<T extends FocusableElement = HTMLAnchorElement>(
  opts: UseLinkOptions,
) {
  const { isDisabled } = opts;

  const ref = useRef<T>(null);

  const { hoverProps, isHovered } = useHover({
    isDisabled,
  });

  const { isPressed, linkProps } = useAriaLink(opts, ref);

  const mergedProps = mergeProps(linkProps, hoverProps);

  return {
    linkProps: {
      'data-hovered': isHovered,
      'data-pressed': isPressed,
      ref,
      ...mergedProps,
    },
  };
}
